import { supabase } from '@/lib/supabase';

export const MATERIAL_TYPES = ['PDF','WORKBOOK','SLIDES','EXERCISE','STUDY_GUIDE','SUMMARY','LINK','DOCUMENT','OTHER'] as const;
export type LessonMaterial = {
  id: string; tenant_id: string; course_id: string; module_id: string; lesson_id: string;
  material_type: typeof MATERIAL_TYPES[number]; title: string; description: string | null;
  asset_ref: string | null; external_url: string | null;
  source: 'HUMAN_UPLOAD'|'AI_GENERATED'|'DRIVE_IMPORT'|'EXTERNAL_LINK';
  provenance: Record<string, unknown>; status: 'DRAFT'|'PROPOSED'|'APPROVED'|'PUBLISHED'|'REJECTED';
  language: string | null; sort_order: number; mime_type: string | null; size_bytes: number | null;
  original_filename: string | null; version: number; approved_at: string | null; created_at: string;
  resolved_url?: string | null;
};

const BUCKET = 'major-high-materials';
const MAX_BYTES = 25 * 1024 * 1024;
const ALLOWED = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

export function materialStoragePath(assetRef: string | null) {
  return assetRef?.match(/^(?:supabase:\/\/major-high-materials\/|major-high-materials:)(.+)$/i)?.[1] ?? null;
}

async function resolveMaterial(material: LessonMaterial): Promise<LessonMaterial> {
  if (material.external_url) return { ...material, resolved_url: material.external_url };
  const path = materialStoragePath(material.asset_ref);
  if (!path) return { ...material, resolved_url: null };
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
  return { ...material, resolved_url: error ? null : data.signedUrl };
}

export async function listLessonMaterials(lessonId: string, includeReview = false) {
  let query = supabase.from('lesson_materials').select('*').eq('lesson_id', lessonId)
    .order('sort_order').order('created_at');
  if (!includeReview) query = query.in('status', ['APPROVED','PUBLISHED']);
  const { data, error } = await query;
  if (error) throw error;
  return Promise.all(((data ?? []) as LessonMaterial[]).map(resolveMaterial));
}

async function save(input: Partial<LessonMaterial> & { lesson_id: string; material_type: string; title: string; source: string; status: string }) {
  const { data, error } = await supabase.rpc('major_high_save_lesson_material', {
    p_material_id: input.id ?? null, p_lesson_id: input.lesson_id, p_type: input.material_type,
    p_title: input.title, p_description: input.description ?? null, p_asset_ref: input.asset_ref ?? null,
    p_external_url: input.external_url ?? null, p_source: input.source,
    p_provenance: input.provenance ?? {}, p_status: input.status, p_language: input.language ?? 'pt-BR',
    p_sort_order: input.sort_order ?? 0, p_mime_type: input.mime_type ?? null,
    p_size_bytes: input.size_bytes ?? null, p_original_filename: input.original_filename ?? null,
  });
  if (error) throw error;
  return data as LessonMaterial;
}

// Stores a final file for a proposal and returns the ref the finalize RPC publishes.
// No material row is written here: the proposal stays the one record.
export async function uploadMaterialArtifact(input: { tenantId: string; lessonId: string; file: File }) {
  if (!ALLOWED.has(input.file.type)) throw new Error('Use PDF, DOCX ou PPTX. Arquivos executáveis não são aceitos.');
  if (input.file.size > MAX_BYTES) throw new Error('Arquivo muito grande (máximo 25 MB).');
  const ext = input.file.name.split('.').pop()?.toLowerCase();
  if (!ext || !['pdf','docx','pptx'].includes(ext)) throw new Error('Extensão de arquivo não permitida.');
  const path = `${input.tenantId}/${input.lessonId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, input.file, {
    upsert: false, contentType: input.file.type, cacheControl: '3600',
  });
  if (error) throw error;
  return { ref: `supabase://${BUCKET}/${path}`, remove: () => supabase.storage.from(BUCKET).remove([path]) };
}

export async function uploadLessonMaterial(input: { tenantId: string; lessonId: string; file: File; title: string; type: string; description?: string; sortOrder?: number }) {
  if (!ALLOWED.has(input.file.type)) throw new Error('Use PDF, DOCX ou PPTX. Arquivos executáveis não são aceitos.');
  if (input.file.size > MAX_BYTES) throw new Error('Arquivo muito grande (máximo 25 MB).');
  const ext = input.file.name.split('.').pop()?.toLowerCase();
  if (!ext || !['pdf','docx','pptx'].includes(ext)) throw new Error('Extensão de arquivo não permitida.');
  const path = `${input.tenantId}/${input.lessonId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, input.file, {
    upsert: false, contentType: input.file.type, cacheControl: '3600',
  });
  if (error) throw error;
  try {
    return await save({ lesson_id: input.lessonId, material_type: input.type, title: input.title,
      description: input.description ?? null, asset_ref: `supabase://${BUCKET}/${path}`,
      source: 'HUMAN_UPLOAD', status: 'PUBLISHED', provenance: { uploadedBy: 'SECRETARY', originalFilename: input.file.name },
      sort_order: input.sortOrder ?? 0, mime_type: input.file.type, size_bytes: input.file.size,
      original_filename: input.file.name });
  } catch (cause) {
    await supabase.storage.from(BUCKET).remove([path]);
    throw cause;
  }
}

export function addLessonMaterialLink(input: { lessonId: string; url: string; title: string; description?: string; sortOrder?: number }) {
  let url: URL;
  try { url = new URL(input.url); } catch { throw new Error('Informe uma URL válida.'); }
  if (url.protocol !== 'https:') throw new Error('O link deve usar HTTPS.');
  return save({ lesson_id: input.lessonId, material_type: 'LINK', title: input.title,
    description: input.description ?? null, external_url: url.toString(), source: 'EXTERNAL_LINK',
    status: 'PUBLISHED', provenance: { enteredBy: 'SECRETARY' }, sort_order: input.sortOrder ?? 0 });
}

export function saveLessonMaterialMetadata(material: LessonMaterial) {
  return save({ ...material, lesson_id: material.lesson_id, material_type: material.material_type,
    title: material.title, source: material.source, status: material.status });
}

export async function reviewLessonMaterial(id: string, action: 'APPROVE'|'REJECT') {
  const { data, error } = await supabase.rpc('major_high_review_lesson_material', { p_material_id: id, p_action: action });
  if (error) throw error;
  return data as LessonMaterial;
}

export const lessonMaterialGeneratorPort = {
  contractVersion: 'major-high.lesson-material-generator.v1',
  submitProposal: (input: { lessonId: string; type: string; title: string; description?: string; artifactRef?: string; transcriptRef: string; language?: string }) =>
    save({ lesson_id: input.lessonId, material_type: input.type, title: input.title,
      description: input.description ?? null,
      source: 'AI_GENERATED', status: 'PROPOSED', language: input.language ?? 'pt-BR',
      provenance: { artifactRef: input.artifactRef ?? null, transcriptRef: input.transcriptRef,
        generatorContract: 'major-high.lesson-material-generator.v1' } }),
};
